import { PERIOD_LABEL, WEEKDAY_LONG, toISODate } from './dates';
import { LEGAL_VERSION, privacyDueDate } from './legal';
import { EMPTY_PROFILE } from './members';
import { emptyRegistrationData } from './registrations';
import { keepNotified } from './skippedDates';
import { anonymizeMemberInVisitors, visitorMemberId } from './visitors';
import type {
  AccountPrivacy,
  AgeRange,
  AvailState,
  Blackout,
  CommunicationPrefs,
  ContactOutcome,
  DomainState,
  FollowUpKind,
  Member,
  PrivacyRequest,
  PrivacyRequestKind,
  PrivacyRequestStatus,
  Registration,
  ReminderChannel,
  ScheduleEvent,
  SwapStatus,
  Visitor,
  VisitorSource,
} from './types';

/**
 * Direitos do titular (LGPD, art. 18) dentro do Live Gospel: os pedidos que a
 * administração da igreja atende (com prazo de 15 dias), a anonimização de quem pede a
 * exclusão e a cópia dos dados que a própria pessoa baixa.
 */

export const PRIVACY_REQUEST_LABEL: Record<PrivacyRequestKind, { title: string; description: string }> = {
  revoke: {
    title: 'Revogar consentimento',
    description: 'Para dados tratados com a sua autorização, como a participação na igreja.',
  },
  deletion: {
    title: 'Excluir minha conta e dados',
    description: 'Analisada pela administração em até 15 dias.',
  },
  other: {
    title: 'Outro pedido sobre meus dados',
    description: 'Saber com quem foram compartilhados, corrigir o que você não edita…',
  },
};

export const PRIVACY_STATUS_LABEL: Record<PrivacyRequestStatus, string> = {
  open: 'Em análise',
  done: 'Atendido',
  denied: 'Recusado',
};

export const PRIVACY_NOTE_MAX = 1000;

/** Comunicações do acesso novo: tudo ligado, até a pessoa desligar. */
export const DEFAULT_COMMUNICATIONS: CommunicationPrefs = { email: true, whatsapp: true, push: true };

export const COMMUNICATION_LABEL: Record<keyof CommunicationPrefs, { title: string; description: string }> = {
  email: { title: 'E-mail', description: 'Lembretes, cobranças de confirmação e o resumo dos líderes.' },
  whatsapp: { title: 'WhatsApp', description: 'Os mesmos lembretes pelo WhatsApp, quando a igreja ativar.' },
  push: { title: 'Notificações no aparelho', description: 'Avisos de escala no celular ou no computador.' },
};

/**
 * Os canais de Configurações que a pessoa aceita receber. Sem conta (`null`), valem os da
 * igreja; os avisos de acesso (convite, senha) não passam por aqui e sempre saem.
 */
export function allowedChannels(
  channels: readonly ReminderChannel[],
  prefs: Pick<CommunicationPrefs, ReminderChannel> | null
): ReminderChannel[] {
  return prefs ? channels.filter((c) => prefs[c]) : [...channels];
}

/** Conta que nunca aceitou os documentos (acesso criado antes deles). */
export const EMPTY_ACCOUNT_PRIVACY: AccountPrivacy = {
  termsVersion: null,
  termsAcceptedAt: null,
  communications: DEFAULT_COMMUNICATIONS,
};

/** Os pedidos de uma pessoa, do mais novo para o mais antigo. */
export function requestsOf(requests: readonly PrivacyRequest[], memberId: string): PrivacyRequest[] {
  return requests.filter((r) => r.memberId === memberId).sort((a, b) => b.createdAt - a.createdAt);
}

/** Os pedidos em aberto, do prazo mais curto para o mais longo. */
export function openRequests(requests: readonly PrivacyRequest[]): PrivacyRequest[] {
  return requests
    .filter((r) => r.status === 'open')
    .sort((a, b) => a.dueDate.localeCompare(b.dueDate) || a.createdAt - b.createdAt);
}

export function isOverdue(request: PrivacyRequest, today: string): boolean {
  return request.status === 'open' && today > request.dueDate;
}

/** O que impede o pedido: já há um igual em análise, ou "outro" sem dizer o quê. */
export function privacyRequestError(
  requests: readonly PrivacyRequest[],
  memberId: string,
  kind: PrivacyRequestKind,
  note: string
): string | null {
  if (requests.some((r) => r.memberId === memberId && r.kind === kind && r.status === 'open')) {
    return 'Você já tem um pedido assim em análise.';
  }
  if (kind === 'other' && !note.trim()) return 'Conte qual é o seu pedido.';
  if (note.length > PRIVACY_NOTE_MAX) return `A observação passa de ${PRIVACY_NOTE_MAX} caracteres.`;

  return null;
}

export interface PrivacyRequestInput {
  id: string;
  memberId: string;
  kind: PrivacyRequestKind;
  note: string;
  /** Envio (epoch ms) e o dia dele, que conta o prazo. */
  at: number;
  today: string;
}

/** Registra o pedido (confira antes com `privacyRequestError`). */
export function submitPrivacyRequest(
  requests: readonly PrivacyRequest[],
  input: PrivacyRequestInput
): PrivacyRequest[] {
  return [
    ...requests,
    {
      id: input.id,
      memberId: input.memberId,
      kind: input.kind,
      note: input.note.trim(),
      status: 'open',
      createdAt: input.at,
      dueDate: privacyDueDate(input.today),
      resolvedAt: null,
      resolvedBy: '',
      response: '',
      anonymized: false,
    },
  ];
}

export interface PrivacyResolution {
  status: Exclude<PrivacyRequestStatus, 'open'>;
  /** Resposta à pessoa; obrigatória na recusa. */
  response: string;
  /** Na exclusão atendida: anonimiza o cadastro junto. */
  anonymize: boolean;
}

/** O que impede a resposta. `adminIds` = quem é Administrador (não se anonimiza o último). */
export function resolutionError(
  request: PrivacyRequest | undefined,
  resolution: PrivacyResolution,
  context: { actorId: string; adminIds: readonly string[] }
): string | null {
  if (!request) return 'Pedido não encontrado.';
  if (request.status !== 'open') return 'Este pedido já foi respondido.';
  if (resolution.status === 'denied' && !resolution.response.trim()) {
    return 'Explique à pessoa por que o pedido foi recusado.';
  }
  if (resolution.response.length > PRIVACY_NOTE_MAX) {
    return `A resposta passa de ${PRIVACY_NOTE_MAX} caracteres.`;
  }
  if (resolution.anonymize && (resolution.status !== 'done' || request.kind !== 'deletion')) {
    return 'Só a exclusão atendida anonimiza o cadastro.';
  }
  if (resolution.anonymize && request.memberId === context.actorId) {
    return 'Você não pode anonimizar o próprio cadastro. Peça a outro administrador.';
  }
  if (
    resolution.anonymize &&
    context.adminIds.includes(request.memberId) &&
    context.adminIds.filter((id) => id !== request.memberId).length === 0
  ) {
    return 'A igreja precisa de pelo menos um administrador. Defina outro antes de anonimizar.';
  }

  return null;
}

type PrivacyChange = Pick<
  DomainState,
  'members' | 'ministries' | 'events' | 'log' | 'registrations' | 'privacyRequests'
> &
  Partial<Pick<DomainState, 'visitors'>>;

/**
 * Responde ao pedido. Na exclusão atendida com `anonymize`, anonimiza o cadastro
 * (`anonymizeMember`). Confira antes com `resolutionError`.
 */
export function resolvePrivacyRequest<S extends PrivacyChange>(
  state: S,
  requestId: string,
  resolution: PrivacyResolution,
  context: { who: string; at: number; today: string }
): S {
  const request = state.privacyRequests.find((r) => r.id === requestId);

  if (!request || request.status !== 'open') return state;

  const anonymize = resolution.anonymize && resolution.status === 'done' && request.kind === 'deletion';
  const base = anonymize ? anonymizeMember(state, request.memberId, context.today) : state;

  return {
    ...base,
    privacyRequests: state.privacyRequests.map((r) =>
      r.id === requestId
        ? {
            ...r,
            status: resolution.status,
            response: resolution.response.trim(),
            resolvedAt: context.at,
            resolvedBy: context.who,
            anonymized: anonymize,
          }
        : r
    ),
  };
}

/**
 * O que a anonimização faz com cada campo do cadastro — `clear` (some) ou `keep` (fica,
 * com o porquê). É um `Record` sobre os campos de `Member`: **campo novo no cadastro não
 * compila até entrar aqui**, e o teste de `anonymizeMember` confere que cada `clear` some
 * e cada `keep` fica. A cópia dos dados (`personalDataExport`) leva todos os campos.
 */
export const MEMBER_ANONYMIZATION: Record<keyof Member, { action: 'clear' | 'keep'; why: string }> = {
  id: { action: 'keep', why: 'Liga as escalas passadas, que ficam como estatística, sem identificar.' },
  name: { action: 'clear', why: 'Vira "Pessoa anonimizada N".' },
  nickname: { action: 'clear', why: 'Identifica a pessoa (o apelido).' },
  selfUpdatedAt: { action: 'clear', why: 'Registro de uso pela pessoa.' },
  email: { action: 'clear', why: 'Contato e login.' },
  phone: { action: 'clear', why: 'Contato.' },
  birthDate: { action: 'clear', why: 'Identifica a pessoa.' },
  photo: { action: 'clear', why: 'Identifica a pessoa (o rosto).' },
  sex: {
    action: 'keep',
    why: 'Obrigatório no cadastro; sozinho não identifica e mantém a estatística das funções.',
  },
  spouseId: { action: 'clear', why: 'Vínculo com outra pessoa.' },
  spouseText: { action: 'clear', why: 'Nome de outra pessoa.' },
  coupleMode: { action: 'clear', why: 'Depende do cônjuge.' },
  roles: { action: 'clear', why: 'A pessoa deixa de servir.' },
  availability: { action: 'clear', why: 'A pessoa deixa de servir.' },
  blackouts: { action: 'clear', why: 'Motivos pessoais (viagem, saúde).' },
  civilStatus: { action: 'clear', why: 'Dado pessoal.' },
  profession: { action: 'clear', why: 'Dado pessoal.' },
  churchStatus: { action: 'clear', why: 'Dado sensível (convicção religiosa).' },
  memberSince: { action: 'clear', why: 'Dado sensível (convicção religiosa).' },
  joinedVia: { action: 'clear', why: 'Dado sensível (convicção religiosa).' },
  baptismDate: { action: 'clear', why: 'Dado sensível (convicção religiosa).' },
  originChurch: { action: 'clear', why: 'Dado sensível (convicção religiosa).' },
  cpf: { action: 'clear', why: 'Documento: identifica a pessoa sozinho.' },
  address: { action: 'clear', why: 'Dado pessoal privado.' },
  location: { action: 'clear', why: 'Coordenadas do endereço: localizam a casa da pessoa.' },
  emergency: { action: 'clear', why: 'Dado de outra pessoa.' },
  inactiveSince: {
    action: 'keep',
    why: 'Não identifica; a anonimização também inativa quem ainda estava ativo (a pessoa deixa de servir).',
  },
  admin: { action: 'clear', why: 'O perfil volta a Membro: a pessoa deixa de ter acesso.' },
};

export const ANONYMIZED_NAME = 'Pessoa anonimizada';

/** "Pessoa anonimizada 3": o primeiro número livre (o nome do membro é único). */
export function anonymizedName(members: readonly Pick<Member, 'name'>[]): string {
  const used = new Set(members.map((m) => m.name));
  let n = 1;

  while (used.has(`${ANONYMIZED_NAME} ${n}`)) n += 1;

  return `${ANONYMIZED_NAME} ${n}`;
}

export function isAnonymized(member: Pick<Member, 'name'>): boolean {
  return new RegExp(`^${ANONYMIZED_NAME} \\d+$`).test(member.name);
}

/**
 * Anonimiza o cadastro de quem pediu a exclusão. Some tudo o que identifica a pessoa —
 * nome (vira "Pessoa anonimizada N"), foto, contatos, nascimento, endereço, emergência, vida na
 * igreja, cônjuge — e ela deixa de servir: sem funções, disponibilidade nem ausências,
 * fora das escalas de hoje em diante, das lideranças e das regras das funções. O que já
 * aconteceu fica só como estatística (as escalas passadas e o check-in), e o histórico
 * de alterações troca o nome pelo anônimo. O motivo de "Não posso ir" também some.
 *
 * O acesso (senha, sessões, link da agenda) é do servidor, que o apaga junto.
 */
export function anonymizeMember<
  S extends Pick<DomainState, 'members' | 'ministries' | 'events' | 'log' | 'registrations'> &
    Partial<Pick<DomainState, 'visitors'>>,
>(state: S, memberId: string, today: string): S {
  const target = state.members.find((m) => m.id === memberId);

  if (!target) return state;

  const name = anonymizedName(state.members);
  const anonymous: Member = {
    id: target.id,
    name,
    nickname: '',
    email: '',
    phone: '',
    birthDate: null,
    sex: target.sex,
    photo: null,
    spouseId: null,
    spouseText: '',
    coupleMode: null,
    roles: {},
    availability: Array<AvailState>(21).fill('unset'),
    blackouts: [],
    ...EMPTY_PROFILE,
    inactiveSince: target.inactiveSince ?? today,
    selfUpdatedAt: null,
    admin: false,
  };
  const withoutPerson = <T>(record: Record<string, T>): Record<string, T> =>
    Object.fromEntries(Object.entries(record).filter(([id]) => id !== memberId));
  // O pedido de troca cita as duas pessoas: sai inteiro.
  const involvesPerson = (s: { fromId: string; toId: string }) =>
    s.fromId === memberId || s.toId === memberId;
  const rename = (text: string) => (target.name ? text.split(target.name).join(name) : text);

  return {
    ...state,
    members: state.members.map((m) => {
      if (m.id === memberId) return anonymous;
      if (m.spouseId === memberId) return { ...m, spouseId: null, coupleMode: null };

      return m;
    }),
    ministries: state.ministries.map((ministry) => ({
      ...ministry,
      leaderIds: ministry.leaderIds.filter((id) => id !== memberId),
      roles: ministry.roles.map((role) => ({
        ...role,
        leaderIds: role.leaderIds.filter((id) => id !== memberId),
        trainees: role.trainees.filter((id) => id !== memberId),
        blockedPairs: role.blockedPairs.filter((pair) => !pair.includes(memberId)),
        togetherPairs: role.togetherPairs.filter((pair) => !pair.includes(memberId)),
      })),
    })),
    events: state.events.map((original) => {
      // O aviso da data cancelada não cita mais a pessoa; o registro do público troca o nome.
      const event = renameAudience(
        keepNotified(original, (id) => id !== memberId),
        rename
      );
      const involved =
        Object.values(event.slots).some((ids) => ids.includes(memberId)) ||
        memberId in event.responses ||
        memberId in event.attendance ||
        memberId in event.notices ||
        event.swaps.some(involvesPerson);

      if (!involved) return event;
      if (event.date < today) {
        const response = event.responses[memberId];
        const past = {
          ...event,
          notices: withoutPerson(event.notices),
          swaps: event.swaps.filter((s) => !involvesPerson(s)),
        };

        return response?.reason
          ? { ...past, responses: { ...event.responses, [memberId]: { ...response, reason: '' } } }
          : past;
      }

      return {
        ...event,
        slots: Object.fromEntries(
          Object.entries(event.slots).map(([roleId, ids]) => [roleId, ids.filter((id) => id !== memberId)])
        ),
        responses: withoutPerson(event.responses),
        attendance: withoutPerson(event.attendance),
        notices: withoutPerson(event.notices),
        swaps: event.swaps.filter((s) => !involvesPerson(s)),
      };
    }),
    log: state.log.map((entry) => ({ ...entry, who: rename(entry.who), text: rename(entry.text) })),
    registrations: state.registrations.map((r) =>
      r.memberId === memberId ? { ...r, data: { ...emptyRegistrationData(), name } } : r
    ),
    ...(state.visitors
      ? {
          visitors: anonymizeMemberInVisitors(
            state.visitors,
            memberId,
            state.registrations,
            rename,
            Date.parse(`${today}T12:00:00`)
          ),
        }
      : {}),
  };
}

/** Quem registrou ou editou o público do culto guarda só o nome: troca pelo anônimo. */
function renameAudience(event: ScheduleEvent, rename: (text: string) => string): ScheduleEvent {
  const audience = event.audience;

  if (!audience) return event;

  const recordedBy = rename(audience.recordedBy);
  const updatedBy = audience.updatedBy === null ? null : rename(audience.updatedBy);

  if (recordedBy === audience.recordedBy && updatedBy === audience.updatedBy) return event;

  return { ...event, audience: { ...audience, recordedBy, updatedBy } };
}

// --- Cópia dos dados (portabilidade) ---------------------------------------------------

export const EXPORT_FORMAT = 'live-gospel-escalas/dados-pessoais@1';

export interface PersonalDataAccount extends AccountPrivacy {
  createdAt: string | null;
  activatedAt: string | null;
  lastLoginAt: string | null;
}

type ProfileFields = Omit<Member, 'roles' | 'availability' | 'blackouts' | 'spouseId'> & { spouse: string };

/** O cadastro na cópia: sem funções, disponibilidade e ausências (vêm à parte), com o cônjuge pelo nome. */
export type ExportedProfile = { [K in keyof ProfileFields]: ProfileFields[K] };

export interface PersonalDataExport {
  format: typeof EXPORT_FORMAT;
  generatedAt: string;
  /** Versão dos documentos em vigor quando a cópia foi gerada. */
  legalVersion: string;
  organization: { name: string; slug: string };
  profile: ExportedProfile;
  roles: { ministry: string; role: string; leader: boolean }[];
  availability: { day: string; period: string; available: boolean }[];
  blackouts: Blackout[];
  schedules: {
    date: string;
    time: string;
    event: string;
    ministry: string;
    role: string;
    published: boolean;
    response: 'confirmed' | 'declined' | 'pending';
    reason: string;
    attendance: 'present' | 'absent' | null;
  }[];
  /** Datas em que serviu no lugar de alguém (sem dizer quem: é dado de outra pessoa). */
  substitutions: { date: string; event: string }[];
  /** Pedidos de troca que fez (`sent`) ou recebeu (`received`), sem o nome do colega. */
  swaps: { date: string; event: string; role: string; direction: 'sent' | 'received'; status: SwapStatus }[];
  privacyRequests: Omit<PrivacyRequest, 'memberId'>[];
  /**
   * A ficha de visitante de onde o cadastro veio (encaminhada e aprovada), sem o que é de
   * outras pessoas (quem atendeu, quem convidou); `null` sem ficha ou fora do recorte.
   */
  visitorRecord: {
    visits: string[];
    source: VisitorSource;
    ageRange: AgeRange;
    district: string;
    wantsContact: boolean;
    consentAt: string;
    contacts: { at: string; kind: FollowUpKind; outcome: ContactOutcome }[];
  } | null;
  /** O acesso (só com o servidor). */
  account: PersonalDataAccount | null;
  /** Lembretes e cobranças enviados (só com o servidor). */
  messages: { kind: string; at: string; channels: string[] }[];
}

/**
 * A cópia completa dos dados de uma pessoa, em JSON (LGPD, art. 18, II e V): o cadastro,
 * as funções, a disponibilidade, as ausências, as escalas com a resposta e o check-in, e
 * os pedidos de privacidade. Sem dados de outras pessoas além do nome do cônjuge.
 */
export function personalDataExport(
  state: Pick<DomainState, 'members' | 'ministries' | 'events' | 'privacyRequests'> &
    Partial<Pick<DomainState, 'visitors' | 'registrations'>>,
  memberId: string,
  context: {
    organization: { name: string; slug: string };
    generatedAt: Date;
    account?: PersonalDataAccount | null;
    messages?: PersonalDataExport['messages'];
  }
): PersonalDataExport | null {
  const member = state.members.find((m) => m.id === memberId);

  if (!member) return null;

  const { roles, availability, blackouts, spouseId, ...profile } = member;
  const spouse = state.members.find((m) => m.id === spouseId)?.name ?? member.spouseText;
  const roleOf = new Map(
    state.ministries.flatMap((m) => m.roles.map((r) => [r.id, { ministry: m.name, role: r.name }] as const))
  );

  return {
    format: EXPORT_FORMAT,
    generatedAt: context.generatedAt.toISOString(),
    legalVersion: LEGAL_VERSION,
    organization: context.organization,
    profile: { ...profile, spouse },
    roles: state.ministries.flatMap((m) =>
      m.roles
        .filter((r) => (roles[m.id] ?? []).includes(r.id))
        .map((r) => ({
          ministry: m.name,
          role: r.name,
          leader: r.leaderIds.includes(memberId) || m.leaderIds.includes(memberId),
        }))
    ),
    availability: availability.flatMap((value, index) =>
      value === 'unset'
        ? []
        : [
            {
              day: WEEKDAY_LONG[Math.floor(index / 3)]!,
              period: PERIOD_LABEL[index % 3]!,
              available: value === 'available',
            },
          ]
    ),
    blackouts,
    schedules: state.events.flatMap((event) =>
      Object.entries(event.slots)
        .filter(([, ids]) => ids.includes(memberId))
        .map(([roleId]) => {
          const response = event.responses[memberId];

          return {
            date: event.date,
            time: event.time,
            event: event.title,
            ministry: roleOf.get(roleId)?.ministry ?? '',
            role: roleOf.get(roleId)?.role ?? '',
            published: event.published,
            response: response?.status ?? 'pending',
            reason: response?.reason ?? '',
            attendance: event.attendance[memberId]?.status ?? null,
          };
        })
    ),
    substitutions: state.events.flatMap((event) =>
      Object.values(event.attendance).some((a) => a.substituteId === memberId)
        ? [{ date: event.date, event: event.title }]
        : []
    ),
    swaps: state.events.flatMap((event) =>
      event.swaps
        .filter((s) => s.fromId === memberId || s.toId === memberId)
        .map((s) => ({
          date: event.date,
          event: event.title,
          role: roleOf.get(s.roleId)?.role ?? '',
          direction: s.fromId === memberId ? ('sent' as const) : ('received' as const),
          status: s.status,
        }))
    ),
    privacyRequests: requestsOf(state.privacyRequests, memberId).map(({ memberId: _, ...r }) => r),
    visitorRecord: visitorRecordOf(state.visitors ?? [], state.registrations ?? [], memberId),
    account: context.account ?? null,
    messages: context.messages ?? [],
  };
}

function visitorRecordOf(
  visitors: readonly Visitor[],
  registrations: readonly Registration[],
  memberId: string
): PersonalDataExport['visitorRecord'] {
  const visitor = visitors.find((v) => !v.anonymized && visitorMemberId(v, registrations) === memberId);

  if (!visitor) return null;

  return {
    visits: visitor.visits.map((v) => v.date),
    source: visitor.source,
    ageRange: visitor.ageRange,
    district: visitor.district,
    wantsContact: visitor.wantsContact,
    consentAt: new Date(visitor.consent.at).toISOString(),
    contacts: visitor.contacts.map((c) => ({
      at: new Date(c.at).toISOString(),
      kind: c.kind,
      outcome: c.outcome,
    })),
  };
}

/** Nome do arquivo da cópia: `meus-dados-live-gospel-2026-10-15.json`. */
export function exportFileName(generatedAt: Date): string {
  return `meus-dados-live-gospel-${toISODate(generatedAt)}.json`;
}
